<template>
  <div class="relative bg-emerald-400 header-stats-wrap">
    <div class="px-4 md:px-10 mx-auto w-full">
      <div class="flex justify-between items-center mb-4">
        <div class="md:hidden flex justify-end w-full">
          <button class="budget-toggle-btn" @click="toggleBudget" type="button">
            <i
              class="fas mr-2"
              :class="mobileBudgetOpen ? 'fa-chevron-up' : 'fa-chevron-down'"
            ></i>
            {{ mobileBudgetOpen ? "Hide budget summary" : "Show budget summary" }}
          </button>
        </div>

        <div class="hidden md:flex justify-end w-full">
          <button class="budget-toggle-btn" @click="toggleDesktopBudget" type="button">
            <i
              class="fas mr-2"
              :class="desktopBudgetHidden ? 'fa-chevron-down' : 'fa-chevron-up'"
            ></i>
            {{ desktopBudgetHidden ? "Show budget summary" : "Hide budget summary" }}
          </button>
        </div>
      </div>

      <transition name="budget-slide">
        <div
          v-show="shouldShowBudgetSummary"
          class="budget-layout"
        >
          <div class="budget-grid-top">
            <div class="budget-card budget-card-total">
              <div class="budget-label">Total Budget</div>
              <div class="budget-value">{{ formattedTotalBudget }}</div>
            </div>

            <div class="budget-card budget-card-available">
              <div class="budget-label">Available Budget</div>
              <div class="budget-value">{{ formattedAvailableBudget }}</div>
            </div>
          </div>

          <div class="budget-grid-bottom">
            <div class="budget-card budget-card-employee">
              <div class="budget-label">Living Arrangements (310~330)</div>
              <div class="budget-value">{{ formattedLivingPair }}</div>
            </div>

            <div class="budget-card budget-card-vendor">
              <div class="budget-label">
                Employment &amp; Community Participation (331~355)
              </div>
              <div class="budget-value">{{ formattedCommunityPair }}</div>
            </div>

            <div class="budget-card budget-card-other">
              <div class="budget-label">Health &amp; Safety (356~399)</div>
              <div class="budget-value">{{ formattedHealthPair }}</div>
            </div>
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
import axios from "axios";

export default {
  name: "HeaderStats",
  data() {
    return {
      totalBudget: 0,
      availableBudget: 0,
      livingOld: 0,
      livingNew: 0,
      communityOld: 0,
      communityNew: 0,
      healthOld: 0,
      healthNew: 0,
      hasBudget: false,
      mobileBudgetOpen: false,
      desktopBudgetHidden: false,
      windowWidth: typeof window !== "undefined" ? window.innerWidth : 1200,
    };
  },
  computed: {
    isMobile() {
      return this.windowWidth < 768;
    },
    shouldShowBudgetSummary() {
      if (this.isMobile) {
        return this.mobileBudgetOpen;
      }
      return !this.desktopBudgetHidden;
    },
    formattedTotalBudget() {
      return this.formatCurrency(this.totalBudget);
    },
    formattedAvailableBudget() {
      return this.formatCurrency(this.availableBudget);
    },
    formattedLivingPair() {
      return `${this.formatCurrency(this.livingOld)} / ${this.formatCurrency(this.livingNew)}`;
    },
    formattedCommunityPair() {
      return `${this.formatCurrency(this.communityOld)} / ${this.formatCurrency(this.communityNew)}`;
    },
    formattedHealthPair() {
      return `${this.formatCurrency(this.healthOld)} / ${this.formatCurrency(this.healthNew)}`;
    },
  },
  methods: {
    async fetchClientBudget() {
      const token = localStorage.getItem("token");

      try {
        const response = await axios.post(
          `${process.env.VUE_APP_BACKEND_DOMAIN}/api/fetch-client-budget-data`,
          {},
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "multipart/form-data",
            },
          }
        );

        const budget = response?.data?.client_budget;

        if (budget) {
          this.totalBudget = Number(budget.total_budget) || 0;
          this.availableBudget = Number(
            budget.available_budget ?? budget.remaining_budget
          ) || 0;
          this.livingOld = Number(budget.living_old) || 0;
          this.livingNew = Number(budget.living_new) || 0;
          this.communityOld = Number(budget.community_old) || 0;
          this.communityNew = Number(budget.community_new) || 0;
          this.healthOld = Number(budget.health_old) || 0;
          this.healthNew = Number(budget.health_new) || 0;
          this.hasBudget = true;
        } else {
          this.resetBudgetData();
        }
      } catch (error) {
        console.error("Backend error:", error);
        this.resetBudgetData();
      }
    },

    resetBudgetData() {
      this.totalBudget = 0;
      this.availableBudget = 0;
      this.livingOld = 0;
      this.livingNew = 0;
      this.communityOld = 0;
      this.communityNew = 0;
      this.healthOld = 0;
      this.healthNew = 0;
      this.hasBudget = false;
    },

    formatCurrency(value) {
      if (value == null || isNaN(value)) return "$0.00";
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(Number(value) || 0);
    },

    toggleBudget() {
      this.mobileBudgetOpen = !this.mobileBudgetOpen;
    },

    toggleDesktopBudget() {
      this.desktopBudgetHidden = !this.desktopBudgetHidden;
      localStorage.setItem(
        "desktopBudgetHidden",
        this.desktopBudgetHidden ? "1" : "0"
      );
    },

    handleResize() {
      this.windowWidth = window.innerWidth;
      if (!this.isMobile) {
        this.mobileBudgetOpen = false;
      }
    },

    restoreDesktopBudgetPreference() {
      const saved = localStorage.getItem("desktopBudgetHidden");
      this.desktopBudgetHidden = saved === "1";
    },
  },
  mounted() {
    this.restoreDesktopBudgetPreference();
    this.fetchClientBudget();
    window.addEventListener("resize", this.handleResize);
  },
  beforeUnmount() {
    window.removeEventListener("resize", this.handleResize);
  },
};
</script>

<style scoped>
.header-stats-wrap {
  padding-top: 1rem;
  padding-bottom: 1rem;
}

.budget-layout {
  width: 100%;
  max-width: 1180px;
}

.budget-toggle-btn {
  background: rgba(255, 255, 255, 0.95);
  color: #1e293b;
  font-size: 0.85rem;
  font-weight: 700;
  padding: 0.7rem 0.95rem;
  border-radius: 9999px;
  box-shadow: 0 8px 20px rgba(15, 23, 42, 0.12);
}

.budget-grid-top {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 18px;
  margin-bottom: 18px;
}

.budget-grid-bottom {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 18px;
}

.budget-card {
  border-radius: 18px;
  padding: 18px 20px;
  min-height: 112px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  box-sizing: border-box;
  border: 1px solid rgba(100, 116, 139, 0.18);
  box-shadow: 0 8px 22px rgba(15, 23, 42, 0.12);
  transition: transform 0.18s ease, box-shadow 0.18s ease;
  overflow: hidden;
}

.budget-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 14px 30px rgba(15, 23, 42, 0.18);
}

.budget-label {
  font-size: 0.98rem;
  font-weight: 700;
  color: #24324a;
  line-height: 1.25;
}

.budget-value {
  font-size: clamp(1.05rem, 1.5vw, 1.55rem);
  font-weight: 800;
  color: #0f172a;
  line-height: 1.2;
  margin-top: 12px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.budget-card-total {
  background: #f6f1d9;
}

.budget-card-available {
  background: #edf4e6;
}

.budget-card-employee {
  background: #e8c2c2;
}

.budget-card-vendor {
  background: #bfd8a7;
}

.budget-card-other {
  background: #c8d9f2;
}

.budget-slide-enter-active,
.budget-slide-leave-active {
  transition: all 0.28s ease;
  overflow: hidden;
}

.budget-slide-enter-from,
.budget-slide-leave-to {
  opacity: 0;
  transform: translateY(-16px);
  max-height: 0;
}

.budget-slide-enter-to,
.budget-slide-leave-from {
  opacity: 1;
  transform: translateY(0);
  max-height: 1200px;
}

@media (min-width: 768px) {
  .header-stats-wrap {
    padding-top: 8rem;
    padding-bottom: 1.5rem;
  }
}

@media (max-width: 767px) {
  .budget-grid-top,
  .budget-grid-bottom {
    grid-template-columns: 1fr;
  }

  .budget-grid-top {
    margin-bottom: 14px;
  }

  .budget-card {
    min-height: 88px;
    padding: 14px 16px;
    border-radius: 16px;
  }

  .budget-label {
    font-size: 0.88rem;
  }

  .budget-value {
    font-size: 1.15rem;
    margin-top: 8px;
    white-space: normal;
    overflow: visible;
    text-overflow: unset;
  }
}

@media (max-width: 767px) {
  .header-stats-wrap {
    padding-top: 1rem;
    padding-bottom: 1rem;
  }

  .budget-layout {
    width: 100%;
    max-width: 100%;
  }

  .budget-grid-top,
  .budget-grid-bottom {
    grid-template-columns: 1fr;
    gap: 12px;
  }

  .budget-grid-top {
    margin-bottom: 12px;
  }

  .budget-card {
    min-height: auto;
    padding: 14px 14px;
    border-radius: 14px;
  }

  .budget-label {
    font-size: 0.82rem;
    line-height: 1.25;
    word-break: break-word;
  }

  .budget-value {
    font-size: clamp(0.95rem, 4.2vw, 1.1rem);
    margin-top: 6px;
    line-height: 1.2;
    white-space: normal;
    overflow: visible;
    text-overflow: unset;
    word-break: break-word;
  }

  .budget-toggle-btn {
    width: 100%;
    justify-content: center;
    text-align: center;
    padding: 0.8rem 1rem;
    font-size: 0.82rem;
  }
}

</style>
